import type { KeyboardEvent } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { Form } from "@/components/ui/form";

import { TextInput } from "@/components/shared/form/text-input";
import { useUpdateJournalEntry } from "@/queries/ledger/entries/update";
import { zodResolver } from "@hookform/resolvers/zod";
import { NavigationElement } from "./navigation";

type DocumentNumberFormProps = {
  journalEntryId: string;
  documentNumber: string | null;
  disabled: boolean;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
};

const formSchema = z.object({
  journalEntryId: z.string().min(1).max(255),
  documentNumber: z.string().min(1).max(255).nullable(),
});

type FormValues = z.infer<typeof formSchema>;

export const DocumentNumberForm = ({
  journalEntryId,
  documentNumber,
  onKeyDown,
  disabled,
}: DocumentNumberFormProps) => {
  const [updateJournalEntry] = useUpdateJournalEntry();

  const handleSubmit = async (data: FormValues) => {
    console.log(data);
    await updateJournalEntry({
      journalEntryId,
      documentNumber: data.documentNumber,
    });
  };

  const { t } = useTranslation();

  const form = useForm<FormValues>({
    defaultValues: {
      journalEntryId,
      documentNumber,
    },
    values: {
      journalEntryId,
      documentNumber,
    },
    resolver: zodResolver(formSchema),
  });

  return (
    <div className="flex items-center gap-2">
      <div className="text-xs text-gray-500">
        {t("transactions.bank_bookings.table.document_number")}
      </div>
      <div className="flex-1 flex items-center gap-2">
        <Form {...form}>
          <NavigationElement>
            <TextInput
              name="documentNumber"
              className="w-full h-8"
              onKeyDown={onKeyDown}
              readOnly={disabled}
            />
          </NavigationElement>
          <AutoSaveForm
            onSubmit={form.handleSubmit(handleSubmit)}
            debounceTime={100}
          />
        </Form>
      </div>
    </div>
  );
};
